import React, { useState, useEffect } from 'react';
import Hero from './Hero';
import {FloatingIcon} from './FloatingIcon';
import { useMediaQuery } from 'react-responsive';

import { ReactComponent as BigBlueLandingBowtie } from '../images/big-blue-landing-bowtie.svg';
import { ReactComponent as BagCheck } from '../images/bag-check.svg';
import { ReactComponent as CreditCard } from '../images/credit-card.svg';
import { ReactComponent as CashCoin } from '../images/cash-coin.svg';
import { ReactComponent as BowTieMobile } from '../images/bowtie-img-mobile.svg';
import { ReactComponent as FakeBrowser } from '../images/Browser.svg';
import { ReactComponent as FakeBrowserMobile } from '../images/Browser-Mobile.svg';
import SocialShareCards from './LandingSocialCards';
import AIChatOverview from './AIChatOverview';

import BowTie from '../images/bowtie-img.png';
import BowTieTablet from '../images/bowtie-landing-tablet.png';
import RecommendationsMobile from '../images/recommendations-mobile.png';
import RecommendationsTablet from '../images/landing-recommendations-tablet.png';
import { NavigationProvider, useNavigation } from '../context/NavigationContext';

const BrowserSVG = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const [mobile, setMobile] = useState(isMobile);

  useEffect(() => {
    setMobile(isMobile);
  }, [isMobile]);


  return (
    <>
      {mobile ? (
        <FakeBrowserMobile className="overview-gif" />
      ) : (
          <FakeBrowser className="overview-gif" />
        )}
    </>
  );
};

const HowItWorksText = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const [mobile, setMobile] = useState(isMobile);
  const isTablet = useMediaQuery({ query: '(max-width: 744px)' });
  const [tablet, setTablet] = useState(isTablet);

  const text = {
    default: {
      card1: {
        heading: "Shop as usual",
        description: "We’ve got all the top credit cards and we know the ins and outs of credit card rewards."
      },
      card2: {
        heading: "We play the rewards game for you",
        description: "We let you know what credit card has the best rewards at checkout."
      },
      card3: {
        heading: "Maximize rewards",
        description: "You rack up points to spend on things that matter to you!"
      }
    },
    tablet: {
      card1: {
        heading: "Shop as usual",
        description: "We’ve got all the top credit cards and we know the ins and outs of credit card rewards."
      },
      card2: {
        heading: "Rewards on us",
        description: "We let you know what credit card has the best rewards at checkout."
      },
      card3: {
        heading: "Maximize rewards",
        description: "You rack up points to spend on things that matter to you!"
      }
    },
    mobile: {
      card1: {
        heading: "Shop as usual",
        description: "We’ve got all the top credit cards and we know the ins and outs of credit card rewards."
      },
      card2: {
        heading: "We play the rewards game for you",
        description: "We let you know what credit card has the best rewards at checkout."
      },
      card3: {
        heading: "Maximize rewards",
        description: "You rack up points to spend on things that matter to you!"
      }
    }
  }

  if (mobile) {
    return text.mobile;
  }
  if (tablet) {
    return text.tablet;
  }
  return text.default;
}

const BowTieSVG = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const [mobile, setMobile] = useState(isMobile);

  const isTablet = useMediaQuery({ query: '(max-width: 744px)' });
  const [tablet, setTablet] = useState(isTablet);

  useEffect(() => {
    setMobile(isMobile);
  }, [isMobile]);

  useEffect(() => {
    setTablet(isTablet);
  }, [isTablet]);


  if (mobile) {
    return(
      <>
        <BowTieMobile
          className="big-blue-bowtie"
          aria-label="Big blue bowtie"
        />
      </>
    )
  }

  if (tablet) {
    return (
      <img
        src={BowTieTablet}
        alt="Big blue bowtie"
        className="big-blue-bowtie"
      />
    )
  }

  return (
    <BigBlueLandingBowtie
      className="big-blue-bowtie"
      aria-label="Big blue bowtie"
    />
  );
}


const LandingPage: React.FC = () => {
  const { navigate } = useNavigation();

  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const [mobile, setMobile] = useState(isMobile);
  const isTablet = useMediaQuery({ query: '(max-width: 744px)' });
  const [tablet, setTablet] = useState(isTablet);

  useEffect(() => {
    setMobile(isMobile);
  }, [isMobile]);

  useEffect(() => {
    setTablet(isTablet);
  }, [isTablet]);

  const cardContent = HowItWorksText();

  return (
    <>
      <div className="landing-page">
        <Hero />

        <SocialShareCards
          onStartSimulation={() => navigate('/simulation')}
        />


        {/* Overview Section */}
        <div className="overview">
          <div className="overview-gif-wrapper">
            <BrowserSVG />
            <FloatingIcon />
          </div>

          <div className="overview-text">
            <h1>We play the rewards game for you</h1>
            <h2>
              Whether you’re a frequent traveler, a dining enthusiast, or a savvy online shopper,
              we’ll guide you to maximize your credit card rewards.
            </h2>
          </div>
        </div>

        {/* AI Connector Section */}
        <AIChatOverview />

        {/* How It Works Section */}
        <div className="how-it-works">
          <BowTieSVG />

          <div className="how-it-works-card-wrapper">
            <h1>How Stashr works</h1>
            <div className="how-it-works-cards-container">
              {/* Card 1 */}
              <div className="how-it-works-card">
                <CreditCard
                  className="how-it-works-icon credit-card-icon"
                  aria-label="Credit card"
                />
                <h2>{cardContent.card1.heading}</h2>
                <p>{cardContent.card1.description}</p>
              </div>
              {/* Card 2 */}
              <div className="how-it-works-card">
                <BagCheck
                  className="how-it-works-icon bag-check-icon"
                  aria-label="Bag check"
                />
                <h2>{cardContent.card2.heading}</h2>
                <p>{cardContent.card2.description}</p>
              </div>
              {/* Card 3 */}
              <div className="how-it-works-card">
                <CashCoin
                  className="how-it-works-icon cash-coin-icon"
                  aria-label="Cash coin"
                />
                <h2>{cardContent.card3.heading}</h2>
                <p>{cardContent.card3.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations Section */}
        <div className="recommendations-wrapper">
          <div className="recommendations">
            <div className="recommendations-text">
              <h1>Personalized recommendations</h1>
              <h2>
                Offers, bonuses and rewards customized for you.  Tailored recommendations to help you save more money and grow your rewards stash.
              </h2>
            </div>
          </div>
          <div className="recommendations-bowtie-wrapper">
            <picture>
              <source media="(max-width: 480px)" srcSet={RecommendationsMobile} />
              <source media="(max-width: 744px)" srcSet={RecommendationsTablet} />
              <img
                src={BowTie}
                alt="Big blue bowtie"
                className="recommendations-bowtie"
              />
            </picture>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
